import { Flex, Icon, Box, Text, Avatar } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { Eye, Outreach as OutreachBold } from '@/assets/icons/bold';
import { SanitizedText } from '@/ui/SanitizedDom';
import { Time } from '@/ui/Time';
import { Paper } from 'react-iconly';
import {
  Bounced,
  MessageReplied as MessageRepliedIcon,
} from '@/assets/icons/bold';

import {
  GetIntro as GetIntroIcon,
  IntroDenied as IntroDeniedIcon,
  IntroMade as IntroMadeIcon,
  ProfileCircle,
  MailScheduled,
  Mail,
} from '@/assets/icons/bold';

import { Restart } from '@/assets/icons/line';
import { conversationConstants } from '../emails/constants';
import { ProjectAdd, ProjectRemove } from '@/assets/icons/projects';

const ActivityCard = ({
  children,
  icon,
  iconBgColor,
  iconColor,
  iconWidth = '20px',
  iconHeight = '20px',
}) => {
  return (
    <Flex>
      <Flex flexDir="column" alignItems="center">
        <Flex
          width="36px"
          height="36px"
          bg={iconBgColor}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
          flexDir="column"
        >
          {icon && (
            <Icon
              as={icon}
              width={iconWidth}
              height={iconHeight}
              color={iconColor}
              set="bold"
            />
          )}
        </Flex>
        <Box className="border" flex="1" borderColor="light.border"></Box>
      </Flex>
      <Box
        flex="1"
        bg="secondary-25"
        borderRadius="14px"
        p="20px"
        ml="12px"
        mb="30px"
      >
        {children}
      </Box>
    </Flex>
  );
};

export const HtmlText = ({ html }) => {
  const history = useHistory();

  const onClickHtml = (e) => {
    const targetLink = e.target.closest('a');
    if (!targetLink) return;
    const hrefURL = new URL(targetLink?.href);
    const pageURL = new URL(window.location);
    if (!targetLink || hrefURL.host !== pageURL.host) return;
    const { pathname } = new URL(targetLink.href);
    history.push(pathname);
    e.preventDefault();
  };
  const contentColor = '#503BF0';
  // note: BE is sending the html code, a tag as string, sanitized to allow only our domains for now
  const sanitizedString = html?.replace(
    /<A\s+HREF="([^"]*)">([^<]+)<\/A>/gi,
    (match, href, content) => {
      // Check if href is not empty and not null
      if (href && href !== '/') {
        // Remove the domain part from the URL
        const path = href.replace(/^(?:\/\/|[^/]+)*\//, '/');

        // Return the content with the replaced href attribute
        return path === '/'
          ? content
          : `<A style="color: ${contentColor}" HREF="${path}">${content}</A>`;
      } else {
        // If href is empty, null, or doesn't contain any path, remove the entire <A> tag
        return content;
      }
    }
  );

  return (
    <SanitizedText
      as="span"
      className="html-text"
      onClick={onClickHtml}
      html={sanitizedString}
    />
  );
};

const MessageScheduled = ({ activity }) => {
  return (
    <ActivityCard
      icon={MailScheduled}
      iconBgColor="#E2EDFF"
      iconColor="#2D64BC"
      iconWidth="16px"
    >
      <HtmlText html={activity?.event} />
    </ActivityCard>
  );
};

const MessageBounced = ({ activity, icon = Mail }) => {
  return (
    <ActivityCard
      icon={icon}
      iconBgColor="#FFEBE7"
      iconColor="#EB4A27"
      iconWidth="22px"
    >
      <HtmlText html={activity?.event} />
    </ActivityCard>
  );
};

const MessageReplied = ({ activity, icon = MessageRepliedIcon }) => {
  return (
    <ActivityCard
      icon={icon}
      iconBgColor="#FFEBE7"
      iconColor="#EB4A27"
      iconWidth="20px"
      iconHeight="20px"
    >
      <HtmlText html={activity?.event} />
    </ActivityCard>
  );
};

const MessageSent = ({ activity }) => {
  if (!activity.event_data) {
    return <></>;
  }
  return (
    <ActivityCard
      icon={conversationConstants[activity?.event_data?.channel]?.icon}
      iconBgColor={conversationConstants[activity?.event_data?.channel]?.bg}
      iconColor="#EB4A27"
      iconWidth="16px"
    >
      <HtmlText html={activity?.event} />
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">Sent by {activity?.user?.name}</Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const ContactCreate = ({ activity }) => {
  return (
    <ActivityCard
      icon={ProfileCircle}
      iconBgColor="#EAF2FF"
      iconColor="#0B4DB7"
    >
      <Text>{activity?.event}</Text>
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">Created by {activity?.user?.name}</Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const NoteAdded = ({ activity }) => {
  return (
    <ActivityCard icon={Paper} iconBgColor="#FEE8C5" iconColor="#FF9C00">
      <Text>{activity?.event_body}</Text>
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Box ml="4px">
          <HtmlText html={activity?.event} />
        </Box>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const GetIntro = ({ activity }) => {
  return (
    <ActivityCard icon={GetIntroIcon} iconBgColor="#EBE4FF" iconColor="#FF9C00">
      <Text>{activity?.event}</Text>
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">Requested by {activity?.user?.name}</Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const DenyIntro = ({ activity }) => {
  return (
    <ActivityCard icon={IntroDeniedIcon} iconBgColor="#FFEBE7">
      <Text>{activity?.event}</Text>
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">Updated by {activity?.user?.name}</Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const MakeIntro = ({ activity }) => {
  return (
    <ActivityCard
      icon={IntroMadeIcon}
      iconBgColor="#EBE4FF"
      iconColor="#FF9C00"
    >
      <HtmlText html={activity?.event} />
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">Updated by {activity?.user?.name}</Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const Vouch = ({ activity }) => {
  if (!activity.event_data) {
    return <></>;
  }
  return (
    <ActivityCard
      icon={IntroMadeIcon}
      iconBgColor="#EBE4FF"
      iconColor="#FF9C00"
    >
      <HtmlText html={activity?.event} />
      <Box>
        {activity?.event_data?.relationship && (
          <Box>
            <Text size="sm-medium" as="span">
              Relationship -
            </Text>
            <Text as="span" ml="4px">
              {activity?.event_data?.relationship}
            </Text>
          </Box>
        )}

        {activity?.event_data?.note && (
          <Box>
            <Text size="sm-medium" as="span">
              Comment -
            </Text>
            <Text as="span" ml="4px">
              {activity?.event_data?.note}
            </Text>
          </Box>
        )}
      </Box>
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">Updated by {activity?.user?.name}</Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const Outreach = ({
  activity,
  iconBgColor = '#FEE8C5',
  iconColor = '#FF9C00',
  icon = OutreachBold,
  actionText = 'Updated by',
}) => {
  return (
    <ActivityCard icon={icon} iconBgColor={iconBgColor} iconColor={iconColor}>
      <HtmlText html={activity?.event} />
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">
          {actionText} {activity?.user?.name}
        </Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

const ProjectReviewActivity = ({
  activity,
  iconBgColor = '#FEE8C5',
  iconColor = '#FF9C00',
  icon = Eye,
  actionText = 'Updated by',
}) => {
  return (
    <ActivityCard icon={icon} iconBgColor={iconBgColor} iconColor={iconColor}>
      <HtmlText html={activity?.event} />
      <Flex alignItems="center" mt="12px">
        <Avatar
          borderRadius="4px"
          className="avatar-sm"
          name={activity?.user?.name}
          width="20px"
          height="20px"
          src={activity?.user?.avatar}
        />
        <Text ml="4px">
          {actionText} {activity?.user?.name}
        </Text>
        <Box ml="4px">
          <Time variant="muted" time={activity?.event_time} />
        </Box>
      </Flex>
    </ActivityCard>
  );
};

export const Activity = ({ activity }) => {
  if (!activity.event) {
    return <></>;
  }
  if (activity?.event_type === 'added_to_outreach') {
    return (
      <Box className="card">
        <Outreach
          activity={activity}
          iconBgColor="#D9E9EE"
          iconColor={'#166055'}
        />
      </Box>
    );
  }

  if (activity?.event_type === 'removed_from_outreach') {
    return (
      <Box className="card">
        <Outreach
          activity={activity}
          iconBgColor="#FFEBE7"
          iconColor="#EB4A27"
        />
      </Box>
    );
  }

  if (activity?.event_type === 'paused_from_outreach') {
    return (
      <Box className="card">
        <Outreach
          activity={activity}
          iconBgColor="#FFEBE7"
          iconColor="#EB4A27"
        />
      </Box>
    );
  }

  if (activity?.event_type === 'outreach_stopped') {
    return (
      <Box className="card">
        <Outreach
          activity={activity}
          iconBgColor="#FFEBE7"
          iconColor="#EB4A27"
        />
      </Box>
    );
  }

  if (activity?.event_type === 'resumed_outreach') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }

  if (
    activity?.event_type === 'message_scheduled' ||
    activity?.event_type === 'message_rescheduled'
  ) {
    return (
      <Box className="card">
        <MessageScheduled activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'note_added') {
    return (
      <Box className="card">
        <NoteAdded activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'candidate_interested') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'candidate_not_interested') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'flow_unsubscribed') {
    return (
      <Box className="card">
        <MessageBounced activity={activity} icon={Bounced} />
      </Box>
    );
  }

  // Ats stages update if Ats not integrated
  if (activity?.event_type === 'candidate_in_review') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_screened') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_screen_rejected') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_team_screened') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_team_screen_rejected') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_onsite') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_onsite_rejected') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_offered') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_offer_rejected') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_hired') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }
  if (activity?.event_type === 'candidate_rejected') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }

  // Ats not linked but updated stage in ATS
  if (activity?.event_type === 'ats_stage_updated') {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'message_sent') {
    return (
      <Box className="card">
        <MessageSent activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'message_replied') {
    return (
      <Box className="card">
        <MessageReplied activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'message_bounced') {
    return (
      <Box className="card">
        <MessageBounced activity={activity} icon={Bounced} />
      </Box>
    );
  }

  if (activity?.event_type === 'contact_create') {
    return (
      <Box className="card">
        <ContactCreate activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'get_intro') {
    return (
      <Box className="card">
        <GetIntro activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'denied_intro') {
    return (
      <Box className="card">
        <DenyIntro activity={activity} />
      </Box>
    );
  }

  if (
    activity?.event_type === 'step_skipped' ||
    activity?.event_type === 'step_bypassed'
  ) {
    return (
      <Box className="card">
        <Outreach activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'make_intro') {
    return (
      <Box className="card">
        <MakeIntro activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'vouch') {
    return (
      <Box className="card">
        <Vouch activity={activity} />
      </Box>
    );
  }

  if (activity?.event_type === 'restarted_outreach') {
    return (
      <Box className="card">
        <Outreach
          activity={activity}
          icon={Restart}
          iconBgColor="#0039A9"
          actionText="Restarted by"
        />
      </Box>
    );
  }
  if (activity?.event_type === 'prospect_added') {
    return (
      <Box className="card">
        <ProjectReviewActivity
          activity={activity}
          iconBgColor="#EAF2FF"
          iconColor={'#444CE7'}
          actionText="Added by"
          icon={ProjectAdd}
        />
      </Box>
    );
  }
  if (activity?.event_type === 'prospect_removed') {
    return (
      <Box className="card">
        <ProjectReviewActivity
          activity={activity}
          iconBgColor="#FFEAEA"
          iconColor={'#E74444'}
          actionText="Removed by"
          icon={ProjectRemove}
        />
      </Box>
    );
  }
  if (activity?.event_type === 'review_requested') {
    return (
      <Box className="card">
        <ProjectReviewActivity activity={activity} actionText="Sent by" />
      </Box>
    );
  }
  if (activity?.event_type === 'review_interested') {
    return (
      <Box className="card">
        <ProjectReviewActivity
          activity={activity}
          iconBgColor="#D9E9EE"
          iconColor={'#166055'}
        />
      </Box>
    );
  }
  if (
    activity?.event_type === 'review_not_interested' ||
    activity?.event_type === 'review_revoked'
  ) {
    return (
      <Box className="card">
        <ProjectReviewActivity
          activity={activity}
          iconBgColor="#FFEAEA"
          iconColor={'#E74444'}
        />
      </Box>
    );
  }

  return null;
};
